/**
 * src/config/features.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Feature flags for staged rollout.
 *
 * HOW TO USE
 * ──────────
 *   import { FEATURES } from '../../../config/features';
 *   if (!FEATURES.EXTENDED_CHARTS) return null;
 *
 * HOW TO ENABLE
 * ─────────────
 * Set the flag to true here, or override via env var:
 *   REACT_APP_FEATURE_EXTENDED_CHARTS=true
 *
 * SPRINT OWNERSHIP
 * ────────────────
 * Sprint A  — DATA_IMPORT
 * Sprint D  — CANVAS_FORMAT (9:16 / 1:1 / 4:5 export)
 * Sprint C  — EXTENDED_CHARTS (bar-horizontal, bar-stacked, scatter, gauge, …)
 * Sprint B  — TIMELINE_MODE (Data Stream — required for bar-race)
 */

const env = (key: string, def: boolean): boolean => {
  const val = process.env[`REACT_APP_FEATURE_${key}`];
  if (val === 'true')  return true;
  if (val === 'false') return false;
  return def;
};

export const FEATURES = {
  /** Sprint A — CSV / Excel import into any chart type */
  DATA_IMPORT:      env('DATA_IMPORT',      true),

  /** Sprint D — 9:16, 1:1, 4:5 export formats */
  CANVAS_FORMAT:    env('CANVAS_FORMAT',    true),

  /**
   * Sprint C — extended chart types.
   * Gates: bar-horizontal, bar-stacked, scatter, gauge,
   *        radar, waterfall, funnel (and future additions).
   * Set to true to unlock all extended chart tabs.
   */
  EXTENDED_CHARTS:  env('EXTENDED_CHARTS',  true),

  /**
   * Sprint B — Data Stream / Timeline Mode for large datasets.
   * Required in addition to EXTENDED_CHARTS for bar-race.
   */
  TIMELINE_MODE:    env('TIMELINE_MODE',    true),
} as const;

export type FeatureKey = keyof typeof FEATURES;